import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Link2,
  Loader2,
} from 'lucide-react'
import {
  fetchAdminPolicy,
  fetchAdminPolicySignatures,
} from '../../../services/adminPolicyService'

function formatSignedAt(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function AdminPolicySignaturesPage() {
  const { policyId } = useParams()
  const [policy, setPolicy] = useState(null)
  const [signatures, setSignatures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [detail, sigs] = await Promise.all([
        fetchAdminPolicy(policyId),
        fetchAdminPolicySignatures(policyId),
      ])
      setPolicy(detail)
      setSignatures(sigs)
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load this policy document.')
    } finally {
      setLoading(false)
    }
  }, [policyId])

  useEffect(() => {
    load()
  }, [load])

  const copyLink = async () => {
    if (!policy?.link) return
    try {
      await navigator.clipboard.writeText(policy.link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Unable to copy the link automatically.')
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-gray-500">
        <Loader2 size={18} className="mr-2 animate-spin" aria-hidden /> Loading…
      </div>
    )
  }

  if (error && !policy) {
    return (
      <div className="p-6">
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
        <Link to="/admin/policies" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-rapid-orange">
          <ArrowLeft size={15} aria-hidden /> Back to documents
        </Link>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col p-6">
      <Link to="/admin/policies" className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-800">
        <ArrowLeft size={15} aria-hidden /> All documents
      </Link>

      <header className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-gray-900">{policy?.name}</h1>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600">v{policy?.version}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${policy?.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              {policy?.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">{policy?.description}</p>
          {policy?.file?.file_name ? (
            <a
              href={policy.file.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-rapid-orange hover:underline"
            >
              <FileText size={13} aria-hidden /> {policy.file.file_name}
              <ExternalLink size={12} aria-hidden />
            </a>
          ) : null}
        </div>
      </header>

      {policy?.link ? (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <Link2 size={14} className="shrink-0 text-gray-400" aria-hidden />
          <input
            readOnly
            value={policy.link}
            onFocus={(e) => e.target.select()}
            className="w-full truncate bg-transparent text-xs text-gray-700 outline-none"
          />
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-xs font-bold text-gray-700 ring-1 ring-gray-200 transition-colors hover:bg-gray-100"
          >
            {copied ? <Check size={13} className="text-emerald-600" aria-hidden /> : <Copy size={13} aria-hidden />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      ) : null}

      <div className="mt-6 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
        <div className="border-b border-gray-100 px-6 py-4">
          <h2 className="text-sm font-bold text-gray-900">Signatures</h2>
          <p className="text-xs text-gray-500">{signatures.length} recorded</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Signer</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Email</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Version</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Signed at</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">IP</th>
              </tr>
            </thead>
            <tbody>
              {signatures.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    Nobody has signed this document yet.
                  </td>
                </tr>
              ) : (
                signatures.map((sig) => (
                  <tr key={sig.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">{sig.signer_name || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{sig.signer_email}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${Number(sig.version) === Number(policy?.version) ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        v{sig.version}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">{formatSignedAt(sig.signed_at)}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">{sig.ip_address || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
